"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import {
  DEFAULT_BACKGROUND_PATTERN_SETTINGS,
  sanitizeBackgroundPatternSettings,
  type BackgroundPatternSettings,
} from "./backgroundPatternStore"

const SAVE_DEBOUNCE_MS = 300

type BackgroundPatternSettingsContextValue = {
  settings: BackgroundPatternSettings
  setSettings: (patch: Partial<BackgroundPatternSettings>) => void
  resetSettings: () => void
}

const BackgroundPatternSettingsContext =
  createContext<BackgroundPatternSettingsContextValue | null>(null)

const persistSettings = async (settings: BackgroundPatternSettings) => {
  try {
    const response = await fetch("/api/background-pattern-settings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    })
    if (!response.ok) {
      console.error(
        "Failed to save background pattern settings:",
        response.status
      )
    }
  } catch (error) {
    console.error("Failed to save background pattern settings:", error)
  }
}

export const BackgroundPatternSettingsProvider = ({
  initialSettings,
  children,
  persist = true,
}: {
  initialSettings: BackgroundPatternSettings
  children: ReactNode
  // When false, settings changes stay local — no POST to
  // /api/background-pattern-settings, no pagehide flush. Useful for the
  // /compute-breakdowns editor where we want to mutate settings live
  // (e.g. shifting band-boundary positions via sliders) without
  // overwriting savedBackgroundPatternSettings.json globally.
  persist?: boolean
}) => {
  const [settings, setSettingsState] = useState(initialSettings)

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const settingsRef = useRef(settings)
  settingsRef.current = settings
  const isFirstRenderRef = useRef(true)

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }
    if (!persist) return
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null
      void persistSettings(settings)
    }, SAVE_DEBOUNCE_MS)
  }, [settings, persist])

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        if (persist) {
          // Flush any pending change so the user doesn't lose their last edit
          // when navigating away from the page. Skipped when persist=false
          // because the local provider is deliberately throwaway.
          void persistSettings(settingsRef.current)
        }
      }
    }
  }, [persist])

  const setSettings = useCallback(
    (patch: Partial<BackgroundPatternSettings>) => {
      setSettingsState((previousSettings) =>
        sanitizeBackgroundPatternSettings({ ...previousSettings, ...patch })
      )
    },
    []
  )

  const resetSettings = useCallback(() => {
    setSettingsState(DEFAULT_BACKGROUND_PATTERN_SETTINGS)
  }, [])

  const contextValue = useMemo<BackgroundPatternSettingsContextValue>(
    () => ({ settings, setSettings, resetSettings }),
    [settings, setSettings, resetSettings]
  )

  return (
    <BackgroundPatternSettingsContext.Provider value={contextValue}>
      {children}
    </BackgroundPatternSettingsContext.Provider>
  )
}

export const useBackgroundPatternSettings = (): BackgroundPatternSettings => {
  const ctx = useContext(BackgroundPatternSettingsContext)
  if (!ctx) return DEFAULT_BACKGROUND_PATTERN_SETTINGS
  return ctx.settings
}

const noopSetSettings = (_patch: Partial<BackgroundPatternSettings>) => {}
const noopResetSettings = () => {}

export const useSetBackgroundPatternSettings = () => {
  const ctx = useContext(BackgroundPatternSettingsContext)
  if (!ctx) return noopSetSettings
  return ctx.setSettings
}

export const useResetBackgroundPatternSettings = () => {
  const ctx = useContext(BackgroundPatternSettingsContext)
  if (!ctx) return noopResetSettings
  return ctx.resetSettings
}
